import {
  type Attachment,
  BOARD_BUCKET_IDS,
  type BoardBucket,
  type BoardOptions,
  type FeedbackSubmission,
  type Invitation,
  type IssueIntake,
  type IssueSummary,
  type MemberPatch,
  type MoveTarget,
  type OrganizationPatch,
  type ProjectCreate,
  type ProjectPatch,
  type ResolvedApprovalStatus,
  type TshirtSize,
} from '@portal/shared';
import { apiDownload, apiRequest, apiUpload, type UploadProgress } from './api';
import { type BoardFilter, boardOptions } from './board-options';
import { API_ROUTES } from './routes';
import type {
  BillingStatementAudit,
  BillingStatementPreview,
  BoardResponse,
  BulkApprovalResult,
  DashboardResponse,
  DeliveredTotals,
  HistoryResponse,
  HistoryRow,
  HoursForecast,
  IngestCredential,
  IntakeResult,
  IssueComment,
  IssueDetail,
  McpConnection,
  MemberRow,
  MonthlyHoursMeter,
  Notification,
  OrganizationAdmin,
  PendingInvitation,
  PlatformUser,
  ProjectAdmin,
  ProjectRef,
  PublishedBillingStatement,
  ReminderPolicy,
  ReminderPolicyResponse,
  ReminderPolicySave,
  SessionUser,
  TimeAuditAllowanceResponse,
  TimeAuditDecisionInput,
  TimeAuditDecisionPreview,
  TimeAuditEntryDetail,
  TimeAuditEntryPage,
  TimeAuditMonth,
  TimeAuditSuggestionRun,
} from './types';
import type {
  WireApprovalResult,
  WireAttentionCounts,
  WireBoard,
  WireBucketCounts,
  WireBulkApprovalResult,
  WireComment,
  WireCompletedSummary,
  WireFeedbackAccepted,
  WireGithubLink,
  WireInvitation,
  WireIssueDetail,
  WireMcpConnection,
  WireMe,
  WireMember,
  WireNotification,
  WireOrganization,
  WirePage,
  WirePendingInvitation,
  WireProject,
  WireUser,
} from './wire';

/**
 * Every call the SPA makes to the API.
 *
 * Paths come from `routes.ts` and nowhere else — see the note there. This module is also the
 * one place the API's wire shapes become the view shapes the screens read: envelopes are
 * unwrapped, `numeric` strings become numbers, and the handful of fields the API names
 * differently are renamed once here rather than in every component.
 */

/**
 * Query keys are declared here rather than inline so a key is never rebuilt from a fresh
 * object literal in a render — a new identity refetches on every pass.
 */
export const queryKeys = {
  me: ['me'] as const,
  githubLink: ['me', 'github'] as const,
  organizations: ['organizations'] as const,
  projects: ['projects'] as const,
  adminProjects: ['admin', 'projects'] as const,
  adminOrganizations: ['admin', 'organizations'] as const,
  members: ['admin', 'members'] as const,
  invitations: ['admin', 'invitations'] as const,
  users: ['admin', 'users'] as const,
  reminderPolicy: ['admin', 'reminder-policy'] as const,
  timeAuditSummary: (month: string) => ['admin', 'time-audit', 'summary', month] as const,
  timeAuditEntries: (params: TimeAuditListParams) =>
    [
      'admin',
      'time-audit',
      'entries',
      params.month,
      params.project,
      params.issue,
      params.origin,
      params.review,
      params.session,
      params.queue,
      params.cursor,
    ] as const,
  timeAuditEntry: (id: string) => ['admin', 'time-audit', 'entry', id] as const,
  timeAuditAllowance: (month: string) => ['admin', 'time-audit', 'allowance', month] as const,
  billingStatementPreview: (month: string) =>
    ['admin', 'time-audit', 'statement-preview', month] as const,
  notifications: ['notifications'] as const,
  // Its own key, not a slice of the list: the count is polled and the list is not, so sharing
  // one key would refetch the expensive half every thirty seconds.
  unreadCount: ['notifications', 'unread-count'] as const,
  board: (projectId: string, options: BoardOptions | BoardFilter = {}) =>
    ['board', projectId, boardOptions(options)] as const,
  history: (params: HistoryParams) =>
    ['history', params.projectId, params.search, params.page, params.pageSize] as const,
  /**
   * The term ONLY. Not the viewer's projects: the API resolves those from the session, so
   * putting them in the key would cache the same answer under two names and refetch on every
   * keystroke that follows a switch.
   */
  issueSearch: (term: string) => ['issue-search', term] as const,
  issue: (issueId: string) => ['issue', issueId] as const,
  /**
   * The detail addressed the way the route is. A SEPARATE key from `issue` above rather than
   * the same one, because the page has no row id until this query resolves — keying it by an
   * id it does not yet hold is not possible, and keying it by an empty string would make every
   * unloaded issue share one cache entry.
   *
   * It shares the `['issue', …]` prefix on purpose, so the sub-resource keys below sort under
   * the same root; `'by-number'` cannot collide with a row id.
   */
  issueByNumber: (projectSlug: string, number: number) =>
    ['issue', 'by-number', projectSlug, number] as const,
  comments: (issueId: string) => ['issue', issueId, 'comments'] as const,
  attachments: (issueId: string) => ['issue', issueId, 'attachments'] as const,
  dashboard: ['dashboard'] as const,
  /**
   * Your own connected applications. Not under `['admin', …]` — every role manages their own,
   * and nesting it there would imply a staff screen that deliberately does not exist.
   */
  mcpConnections: ['mcp-connections'] as const,
  delivered: ['delivered'] as const,
};

// ------------------------------------------------------------------ wire -> view

const toProjectRef = (project: WireProject): ProjectRef => ({
  id: project.id,
  name: project.name,
  slug: project.slug,
  // The API sends owner and repo separately; every screen shows them joined.
  repository: `${project.githubOwner}/${project.githubRepo}`,
});

/**
 * Hours arrive as strings straight from Postgres `numeric`. They become numbers here, at the
 * point where they are only ever formatted — never summed — so nothing downstream is tempted
 * to add two floats that represent money.
 */
const toDeliveredTotals = (summary: WireCompletedSummary): DeliveredTotals => ({
  total: summary.delivered,
  noChargeCount: summary.deliveredNoCharge,
  hoursBilled: Number(summary.hoursBilled),
  hoursNoCharge: Number(summary.hoursNoCharge),
});

const toIssueDetail = (issue: WireIssueDetail): IssueDetail => ({
  ...issue,
  // GitHub's login, which for anything opened through the portal is the App's bot. Who
  // actually asked is `attribution`, carried alongside rather than substituted in here — a
  // screen picks which of the two it is showing instead of having the choice made for it.
  authorName: issue.authorLogin,
  createdAt: issue.githubCreatedAt,
  issueState: issue.state,
  milestone: issue.milestoneTitle,
});

/**
 * WHOSE NAME GOES ON THE COMMENT, in order, and every step of it is load-bearing.
 *
 * 1. THE PORTAL'S OWN RECORD WINS. This is the same rule `IssueByline` holds one screen over —
 *    read the note there for why it has to hold twice. `authorLogin` is GitHub's author, and
 *    for everything written in the portal that is the App: the write-back posts the comment as
 *    the App and the mirror refreshes the column from what GitHub says. Preferring it is why an
 *    entire thread read as `…-portal[bot]`, the client's own replies included.
 *
 * 2. TRIMMED AND TESTED FOR EMPTINESS, not merely for null. An account that never filled in a
 *    name stored `''` here, and `??` would render it — a byline with a blank where the person
 *    goes. The API resolves the label to the writer's email at write time now (`displayName`),
 *    and 0026 repaired the rows written before it did; this is the guard for both.
 *
 * 3. A PORTAL AUTHOR WITH NO LABEL IS NEVER THE APP. `authorUserId` says a person wrote this in
 *    the portal, so falling through to `authorLogin` here would put the bot back on their words
 *    — the original bug, reintroduced by the fallback meant to fix it.
 *
 * 4. A LINKED GITHUB AUTHOR IS NAMED AS THE PORTAL KNOWS THEM. Someone who linked their GitHub
 *    account and belongs to this organization is the same person in the thread wherever they
 *    wrote; the card still says "via GitHub", so where it was written is not lost. The API only
 *    sends this name for a member of the comment's own organization.
 *
 * 5. GITHUB'S LOGIN ONLY FOR COMMENTS ACTUALLY WRITTEN ON GITHUB, where it names a real person.
 */
export function commentAuthor(comment: WireComment): string {
  const named = comment.authorName?.trim();
  if (named !== undefined && named !== '') return named;

  if (comment.authorUserId !== null) return 'Portal user';

  const linked = comment.linkedAuthorName?.trim();
  if (linked !== undefined && linked !== '') return linked;

  const login = comment.authorLogin?.trim();
  return login !== undefined && login !== '' ? login : 'Portal user';
}

/**
 * Written on GitHub, not in the portal: rules 4 and 5 above. A portal comment whose account was
 * later deleted keeps its `authorName` and is not GitHub's, and a row with no name and no login
 * is "Portal user". A linked author is named from the portal but still wrote on GitHub, so the
 * card says both.
 */
export function commentFromGithub(comment: WireComment): boolean {
  return (
    comment.authorUserId === null &&
    (comment.authorName?.trim() ?? '') === '' &&
    (comment.authorLogin?.trim() ?? '') !== ''
  );
}

const toComment = (comment: WireComment): IssueComment => ({
  id: comment.id,
  author: commentAuthor(comment),
  authorRole: comment.authorRole ?? null,
  fromGithub: commentFromGithub(comment),
  body: comment.body,
  createdAt: comment.createdAt,
  mirrored: comment.mirrored,
});

const toHistoryRow = (issue: IssueSummary): HistoryRow => ({
  id: issue.id,
  number: issue.number,
  projectId: issue.projectId,
  title: issue.title,
  kind: issue.kind,
  imported: issue.imported,
  size: issue.size,
  // Both, unresolved. Collapsing them here is what let an estimate be printed as though it
  // were billable time, under a total that counts only actuals.
  actualHours: issue.actualHours,
  estimatedHours: issue.estimatedHours,
  noCharge: issue.noCharge,
  // GitHub's close timestamp, carried on the summary for this column. It used to fall back to
  // `updatedAt`, which meant any portal-side write — a sizing edit, an hours backfill —
  // restamped the delivery date to the day of the edit.
  closedAt: issue.closedAt,
});

// ------------------------------------------------------------------ reads

/**
 * The viewer, and the one deployment fact that arrives with them.
 *
 * The capability flags are kept OUT of `SessionUser` on purpose: they are not properties of the
 * person, and folding them in would invite the next reader to treat them as permissions. They
 * ride along because they come back on the same request, not because they belong to them.
 */
export const fetchMe = (): Promise<{
  user: SessionUser;
  feedbackAvailable: boolean;
  notificationsAvailable: boolean;
  mcpEndpoint: string | null;
}> =>
  apiRequest<WireMe>(API_ROUTES.me.path()).then((me) => ({
    user: {
      id: me.user.id,
      name: me.user.name,
      email: me.user.email,
      role: me.role,
      organizationId: me.organization.id,
      organizationName: me.organization.name,
      // What every issue URL is addressed by. Sent since the endpoint existed — unlike
      // `projectIds` below, there is no deploy window in which it is absent.
      organizationSlug: me.organization.slug,
      projectId: me.projectId,
      /*
       * THE ONE PLACE THE SHAPE IS MADE SAFE, AND WHY IT IS DERIVED RATHER THAN READ.
       *
       * An API that predates multi-project grants sends `projectId` and no `projectIds`. The
       * deploys are not ordered, so this bundle can reach a browser first. `apiRequest` casts
       * the response with no runtime validation, so reading `projectIds` directly would hand
       * every consumer `undefined` and the first `.includes()` would throw inside a `useMemo`
       * above `ErrorPanel` — a white page, for every signed-in user, until the API rolled.
       *
       * Falling back to the single pin keeps that window working rather than merely not
       * crashing.
       */
      projectIds: me.projectIds ?? (me.projectId === null ? [] : [me.projectId]),
    },
    feedbackAvailable: me.feedbackAvailable,
    // `?? false` for the same reason as everywhere else here: an older API omits the field, and
    // the two services deploy unordered. False means no bell rather than a bell polling a 404.
    notificationsAvailable: me.notificationsAvailable ?? false,
    // `?? null` for the same reason: an older API omits it, and the profile page draws the
    // connection instructions only when it has a real endpoint to print. A placeholder would
    // be worse than an absence — somebody would paste it.
    mcpEndpoint: me.mcpEndpoint ?? null,
  }));

/**
 * Rename yourself.
 *
 * Returns the updated user fields so the caller can seed the cache rather than refetch: the
 * session is cached in Redis on the server and `GET /me` would answer with the OLD name for as
 * long as that entry lives, which reads as the form silently failing.
 */
export const updateMyName = (name: string): Promise<{ id: string; name: string }> =>
  apiRequest<{ id: string; name: string }>(API_ROUTES.patchMe.path(), {
    method: API_ROUTES.patchMe.method,
    body: { name },
  });

/** Whether the caller has linked their own GitHub account, and the login to show if so. */
export const fetchGithubLink = (): Promise<WireGithubLink> =>
  apiRequest<WireGithubLink>(API_ROUTES.githubLink.path());

export const fetchMcpConnections = (): Promise<McpConnection[]> =>
  apiRequest<WirePage<WireMcpConnection>>(API_ROUTES.mcpConnections.path()).then((page) =>
    page.rows.map((row) => ({
      id: row.id,
      clientId: row.clientId,
      name: row.name,
      uri: row.uri,
      scopes: row.scopes,
      createdAt: row.createdAt,
    })),
  );

export const revokeMcpConnection = (id: string): Promise<void> =>
  apiRequest<void>(API_ROUTES.revokeMcpConnection.path(id), {
    method: API_ROUTES.revokeMcpConnection.method,
  });

export const fetchProjects = (): Promise<ProjectRef[]> =>
  apiRequest<WirePage<WireProject>>(API_ROUTES.projects.path()).then((page) =>
    page.rows.map(toProjectRef),
  );

/**
 * Board filters are applied server-side; see the note in `BoardPage`.
 *
 * Composable options and legacy quick filters use the same server predicate, before sorting
 * and capping each bucket. Counts therefore describe every match, including unloaded cards.
 */
export type { BoardFilter } from './board-options';

export const fetchBoard = (
  projectId: string,
  options: BoardOptions | BoardFilter = {},
): Promise<BoardResponse> =>
  apiRequest<WireBoard>(API_ROUTES.board.path(), {
    // An absent sort means latest update; an absent filter means all matching requests.
    query: { project: projectId, ...boardOptions(options) },
  }).then((board) => ({
    project: toProjectRef(board.project),
    live: board.live,
    liveTotals: board.liveTotals,
    completed: { ...toDeliveredTotals(board.completed), recent: board.completed.recent },
  }));

export interface HistoryParams {
  projectId: string;
  search: string;
  page: number;
  pageSize: number;
}

export const fetchHistory = (params: HistoryParams): Promise<HistoryResponse> =>
  apiRequest<WirePage<IssueSummary>>(API_ROUTES.issues.path(), {
    query: {
      project: params.projectId,
      bucket: 'completed',
      q: params.search,
      limit: params.pageSize,
      // The API pages by offset, the table by page number.
      offset: (params.page - 1) * params.pageSize,
    },
  }).then((page) => ({
    items: page.rows.map(toHistoryRow),
    total: page.total,
    page: params.page,
    pageSize: params.pageSize,
  }));

/**
 * Delivery totals across everything, not just the page on screen. Separate from the list
 * above on purpose: the figures cover hundreds of rows the browser never receives, so summing
 * a page would understate them.
 */
export const fetchDelivered = (): Promise<DeliveredTotals> =>
  apiRequest<WireCompletedSummary>(API_ROUTES.dashboardSummary.path()).then(toDeliveredTotals);

/**
 * How many results the search dropdown asks for.
 *
 * Small on purpose. This is a "find the one I mean" control, not a list screen: a reader who
 * needs to page through matches wants History or the board, and a dropdown that scrolls past
 * the fold stops being faster than either.
 */
export const SEARCH_LIMIT = 8;

/**
 * The longest term the API will take — `ListQuerySchema` caps `q` at 200 characters and answers
 * 400 above it. Stated here so the box can stop the reader at the ceiling rather than let them
 * paste past it and read the refusal as a broken search.
 */
export const SEARCH_TERM_LIMIT = 200;

/**
 * Find any issue the viewer can reach, by number, by partial title, or by a phrase in the body.
 *
 * NO `project` PARAMETER, and that is the point of the control: the board and History are both
 * pinned to one project, so an issue in a project the reader is not currently looking at is
 * invisible on every screen they have. The API scopes this to the organization and, for a
 * client, intersects it with their granted projects — so "everything the viewer can reach" is
 * decided server-side and cannot be widened from here.
 */
export const searchIssues = (term: string): Promise<IssueSummary[]> =>
  apiRequest<WirePage<IssueSummary>>(API_ROUTES.issues.path(), {
    query: { q: term, limit: SEARCH_LIMIT },
  }).then((page) => page.rows);

export const fetchIssue = (issueId: string): Promise<IssueDetail> =>
  apiRequest<WireIssueDetail>(API_ROUTES.issue.path(issueId)).then(toIssueDetail);

/**
 * The same row, addressed by project slug and GitHub issue number — what the detail page's
 * own URL carries. It returns the whole detail rather than an id to fetch with, so opening a
 * card is one round trip by either address.
 */
export const fetchIssueByNumber = (projectSlug: string, number: number): Promise<IssueDetail> =>
  apiRequest<WireIssueDetail>(API_ROUTES.issueByNumber.path(projectSlug, number)).then(
    toIssueDetail,
  );

export const fetchComments = (issueId: string): Promise<IssueComment[]> =>
  apiRequest<WirePage<WireComment>>(API_ROUTES.issueComments.path(issueId)).then((page) =>
    page.rows.map(toComment),
  );

/**
 * What is attached to a request — metadata only, and never a storage key: the API does not
 * serialize one and the browser has no use for one. The bytes are a second call, per file,
 * made only when a reader asks for them.
 */
export const fetchAttachments = (issueId: string): Promise<Attachment[]> =>
  apiRequest<WirePage<Attachment>>(API_ROUTES.issueAttachmentList.path(issueId)).then(
    (page) => page.rows,
  );

/**
 * One attachment's contents.
 *
 * Both ids travel, and the API checks that the second belongs to the first — pairing an issue
 * the caller may read with an attachment id from another project is a 404 there, and arrives
 * here as `NotFoundError` like any other. Nothing about that is the browser's to enforce; it
 * is stated because the call looks redundant otherwise.
 */
export const fetchAttachmentBytes = (issueId: string, attachmentId: string): Promise<Blob> =>
  apiDownload(API_ROUTES.issueAttachment.path(issueId, attachmentId));

/**
 * Dashboard aggregates stay on separate endpoints because they have separate data contracts.
 * They are requested together so the screen renders in one pass.
 */
export async function fetchDashboard(): Promise<DashboardResponse> {
  const [buckets, summary, forecast, attention, hours, statementResponse] = await Promise.all([
    apiRequest<WireBucketCounts>(API_ROUTES.dashboardBuckets.path()),
    apiRequest<WireCompletedSummary>(API_ROUTES.dashboardSummary.path()),
    apiRequest<HoursForecast>(API_ROUTES.dashboardForecast.path()),
    apiRequest<WireAttentionCounts>(API_ROUTES.dashboardAttention.path()),
    apiRequest<MonthlyHoursMeter>(API_ROUTES.dashboardHours.path()),
    apiRequest<{ statements: PublishedBillingStatement[] }>(API_ROUTES.billingStatements.path(), {
      query: { limit: 12 },
    }),
  ]);

  // Seeded with every bucket at zero. The API sends them all, and a missing key here would
  // render a KPI tile as `undefined` rather than as nothing having happened yet.
  const counts = Object.fromEntries(BOARD_BUCKET_IDS.map((id) => [id, 0])) as Record<
    BoardBucket,
    number
  >;
  for (const bucket of buckets.buckets) counts[bucket.id] = bucket.count;

  return {
    counts,
    delivered: toDeliveredTotals(summary),
    forecast,
    attention,
    hours,
    statements: statementResponse.statements,
  };
}

// ------------------------------------------------------------------ writes

export const submitIntake = (input: IssueIntake): Promise<IntakeResult> =>
  apiRequest<WireIssueDetail>(API_ROUTES.createIssue.path(), {
    method: API_ROUTES.createIssue.method,
    body: input,
  }).then((issue) => ({
    id: issue.id,
    number: issue.number,
    projectId: issue.projectId,
    htmlUrl: issue.htmlUrl,
    kind: issue.kind,
    approvalStatus: issue.approvalStatus,
  }));

/**
 * Feedback, from the widget that is on every screen.
 *
 * The body is the API's own `FeedbackSubmission`, so the two cannot drift: there is no `title`
 * — the GitHub issue's title is composed server-side from the first line of the message — and
 * the endpoint rejects an unknown key outright, which is what stops a well-meant extra field
 * from becoming a 400 nobody can explain.
 */
/**
 * Resolves to nothing, because the API returns nothing to resolve to: the issue lands on the
 * board of the organization that builds Portal, which the sender generally cannot read. A
 * rejected promise is the whole of the failure signal, and it is enough.
 */
export const submitFeedback = (input: FeedbackSubmission): Promise<void> =>
  apiRequest<WireFeedbackAccepted>(API_ROUTES.submitFeedback.path(), {
    method: API_ROUTES.submitFeedback.method,
    body: input,
  }).then(() => undefined);

/**
 * Upload an attachment against an issue that already exists.
 *
 * At intake the caller must treat a failure here as a failed ATTACHMENT and not as a failed
 * request: the issue is already filed by the time this runs, and losing what the client wrote
 * would be far worse than losing a screenshot. `IntakePage` is where that is handled.
 *
 * The API answers 413 for a file over the ceiling, after counting the bytes that arrived — so
 * a file that passed `attachmentProblem` in the browser can still be turned away here, and the
 * message it comes back with is the one worth showing.
 *
 * `onProgress` is threaded rather than optional-by-omission: at 250 MB a client needs to see
 * the thing moving, and the only layer that knows how far along it is is the request itself.
 */
export const uploadAttachment = (
  issueId: string,
  file: File,
  onProgress?: UploadProgress,
): Promise<Attachment> =>
  apiUpload<Attachment>(API_ROUTES.issueAttachments.path(issueId), file, {
    ...(onProgress ? { onProgress } : {}),
  });

/**
 * The approval decision. The issue is addressed in the URL and the decision is the whole
 * body — the API rejects an unknown key, so an `issueId` restated in the body is a 400.
 */
export const submitDecision = (
  issueId: string,
  decision: ResolvedApprovalStatus,
  reason?: string,
): Promise<IssueDetail> =>
  apiRequest<WireApprovalResult>(API_ROUTES.issueApproval.path(issueId), {
    method: API_ROUTES.issueApproval.method,
    // Omitted rather than sent as an empty string. The body is strict and `reason` is
    // optional; `''` would be stored as a reason nobody gave.
    body: reason ? { status: decision, reason } : { status: decision },
  }).then((result) => toIssueDetail(result.issue));

/**
 * Many decisions in one click.
 *
 * The ids are in the body and the response is a per-issue outcome list, never a bare count:
 * a row that lost the race comes back with the decision that WON, and the client has to see
 * that rather than a silently shorter list. A missing or foreign id is not in here at all —
 * it is the uniform 404 for the whole request, so nothing is decided and nothing leaks.
 */
export const submitBulkApproval = (
  issueIds: string[],
  status: ResolvedApprovalStatus,
): Promise<BulkApprovalResult> =>
  apiRequest<WireBulkApprovalResult>(API_ROUTES.bulkApproval.path(), {
    method: API_ROUTES.bulkApproval.method,
    body: { issueIds, status },
  });

/**
 * Staff raising or clearing "we need your input on this". Staff-only, enforced by the API.
 *
 * There is no client-facing counterpart on purpose. Staff clearing the flag on the client's
 * behalf leaves it lit after the client has already answered, and a badge that stays lit after
 * it has been dealt with is one the client learns to ignore; a client dismissing it lets them
 * wave away a question they never answered while staff are still blocked. What clears it is a
 * client COMMENT, server-side, which is the event that actually makes it obsolete.
 */
export const raiseAttention = (issueId: string, note: string): Promise<IssueDetail> =>
  apiRequest<WireIssueDetail>(API_ROUTES.issueAttention.path(issueId), {
    method: API_ROUTES.issueAttention.method,
    body: { action: 'raise', note },
  }).then(toIssueDetail);

/** Staff clearing it by hand: the question was answered somewhere the portal cannot see. */
export const clearAttention = (issueId: string): Promise<IssueDetail> =>
  apiRequest<WireIssueDetail>(API_ROUTES.issueAttention.path(issueId), {
    method: API_ROUTES.issueAttention.method,
    body: { action: 'clear' },
  }).then(toIssueDetail);

/**
 * Resolve an issue the classifier refused, by hand.
 *
 * Staff only, enforced by the API. The response carries the row as it stands afterwards, which
 * is not always the label just sent: the write compare-and-sets against `needs_triage`, so a
 * classifier that got there first wins and `changed` comes back false.
 */
export const submitKind = (
  issueId: string,
  kind: 'maintenance' | 'feature',
  reason?: string,
): Promise<IssueDetail> =>
  apiRequest<{ issue: WireIssueDetail; changed: boolean }>(API_ROUTES.issueKind.path(issueId), {
    method: API_ROUTES.issueKind.method,
    // Omitted rather than sent empty, like `submitDecision` above. The API requires a reason
    // only for the relabel half — overturning a decided label — and rejects an empty string, so
    // sending `''` would turn "finishing triage" into a 400.
    body: reason ? { kind, reason } : { kind },
  }).then((result) => toIssueDetail(result.issue));

/**
 * Move a card to a board column. Staff only.
 *
 * The body carries a BUCKET, not a column value: the board's columns are derived, so the API
 * writes whatever facts produce the target. `planMove` in @portal/shared decides which targets
 * are reachable, and the panel calls the same function — a move the screen offers is one the
 * API accepts, and a 409 here means somebody changed the issue in between.
 */
export const submitMove = (issueId: string, target: MoveTarget): Promise<IssueDetail> =>
  apiRequest<WireIssueDetail>(API_ROUTES.issueMove.path(issueId), {
    method: API_ROUTES.issueMove.method,
    body: { target },
  }).then(toIssueDetail);

/** Staff only, enforced by the API: a client must not set the estimate on their own work. */
export const submitSizing = (
  issueId: string,
  size: TshirtSize,
  estimatedHours: number,
): Promise<IssueDetail> =>
  apiRequest<WireIssueDetail>(API_ROUTES.issueSizing.path(issueId), {
    method: API_ROUTES.issueSizing.method,
    body: { size, estimatedHours },
  }).then(toIssueDetail);

/**
 * Logged hours and the no-charge flag are two endpoints, both staff-only. They are separate
 * decisions server-side — actual hours are billing evidence, waiving them is a commercial
 * choice — so this posts both and returns the row as it stands afterwards.
 */
export async function submitHours(
  issueId: string,
  actualHours: number,
  noCharge: boolean,
): Promise<IssueDetail> {
  await apiRequest<WireIssueDetail>(API_ROUTES.issueActualHours.path(issueId), {
    method: API_ROUTES.issueActualHours.method,
    body: { hours: actualHours },
  });
  const updated = await apiRequest<WireIssueDetail>(API_ROUTES.issueNoCharge.path(issueId), {
    method: API_ROUTES.issueNoCharge.method,
    body: { noCharge },
  });
  return toIssueDetail(updated);
}

/** Absent field means "leave it alone"; `null` clears the value on GitHub. */
export interface GithubFieldPatch {
  state?: 'open' | 'closed';
  assignee?: string | null;
  milestone?: string | null;
  labels?: string[];
}

export const patchGithubFields = (issueId: string, patch: GithubFieldPatch): Promise<IssueDetail> =>
  apiRequest<WireIssueDetail>(API_ROUTES.issueGithub.path(issueId), {
    method: API_ROUTES.issueGithub.method,
    body: patch,
  }).then(toIssueDetail);

export const postComment = (issueId: string, body: string): Promise<IssueComment> =>
  apiRequest<WireComment>(API_ROUTES.createIssueComment.path(issueId), {
    method: API_ROUTES.createIssueComment.method,
    body: { body },
  }).then(toComment);

export const createProject = (input: ProjectCreate): Promise<ProjectRef> =>
  apiRequest<WireProject>(API_ROUTES.createProject.path(), {
    method: API_ROUTES.createProject.method,
    body: input,
  }).then(toProjectRef);

export const sendInvitation = (input: Invitation): Promise<WireInvitation> =>
  apiRequest<WireInvitation>(API_ROUTES.createInvitation.path(), {
    method: API_ROUTES.createInvitation.method,
    body: input,
  });

// ------------------------------------------------------------------ administration

/**
 * Administration's own reads and writes.
 *
 * Deliberately on separate query keys from the rest of the app. `queryKeys.projects` feeds the
 * switcher, the board and intake, and asks the API for LIVE projects only; the key below asks
 * for archived ones too, and the two answers must not overwrite each other in one cache slot.
 */

const toProjectAdmin = (project: WireProject): ProjectAdmin => ({
  ...toProjectRef(project),
  githubOwner: project.githubOwner,
  githubRepo: project.githubRepo,
  githubInstallationId: project.githubInstallationId,
  ingestKeyId: project.ingestKeyId,
  backfillState: project.backfillState,
  backfillImported: project.backfillImported,
  syncedThrough: project.syncedThrough,
  archivedAt: project.archivedAt,
});

export const fetchAdminProjects = (): Promise<ProjectAdmin[]> =>
  apiRequest<WirePage<WireProject>>(API_ROUTES.projects.path(), {
    query: { archived: 'true' },
  }).then((page) => page.rows.map(toProjectAdmin));

export const patchProject = (id: string, patch: ProjectPatch): Promise<ProjectAdmin> =>
  apiRequest<WireProject>(API_ROUTES.patchProject.path(id), {
    method: API_ROUTES.patchProject.method,
    body: patch,
  }).then(toProjectAdmin);

export const deleteProject = (id: string): Promise<void> =>
  apiRequest<void>(API_ROUTES.deleteProject.path(id), {
    method: API_ROUTES.deleteProject.method,
  });

export interface ResyncOutcome {
  queued: boolean;
  /** Present when nothing was enqueued, and why. Today the only value is 'already-running'. */
  reason?: string;
}

export interface ClassifyOutcome {
  /** Jobs actually dispatched. Not the number of rows found — see `found`. */
  queued: number;
  /** Rows whose job was already in flight, so the press was redundant rather than lost. */
  running: number;
  /**
   * Rows the work-list returned. `queued + running`, and the two are worth reporting apart:
   * `found` above `queued` used to be the SILENT case — the response reported the rows found
   * and dispatched nothing.
   */
  found: number;
  limit: number;
}

/** Classify everything in the project that no classifier has answered on. Staff-only. */
export const classifyProject = (id: string): Promise<ClassifyOutcome> =>
  apiRequest<ClassifyOutcome>(API_ROUTES.classifyProject.path(id), {
    method: API_ROUTES.classifyProject.method,
  });

/** Re-price everything already classified, under the current guidance. Never re-labels. */
export const reestimateProject = (id: string): Promise<ClassifyOutcome> =>
  apiRequest<ClassifyOutcome>(API_ROUTES.reestimateProject.path(id), {
    method: API_ROUTES.reestimateProject.method,
  });

/** Apply proposals written before acceptance became automatic. Costs nothing; calls no model. */
export const applyEstimates = (id: string): Promise<{ applied: number }> =>
  apiRequest<{ applied: number }>(API_ROUTES.applyEstimates.path(id), {
    method: API_ROUTES.applyEstimates.method,
  });

export interface CancelRunOutcome {
  cancelled: number;
  /** Left alone because a worker is mid-call on them. */
  running: number;
}

/** Stop a bulk run that has not finished. */
export const cancelProjectRun = (id: string): Promise<CancelRunOutcome> =>
  apiRequest<CancelRunOutcome>(API_ROUTES.cancelProjectRun.path(id), {
    method: API_ROUTES.cancelProjectRun.method,
  });

export const resyncProject = (id: string): Promise<ResyncOutcome> =>
  apiRequest<ResyncOutcome>(API_ROUTES.resyncProject.path(id), {
    method: API_ROUTES.resyncProject.method,
  });

export const fetchAdminOrganizations = (): Promise<OrganizationAdmin[]> =>
  apiRequest<WirePage<WireOrganization>>(API_ROUTES.organizations.path(), {
    query: { archived: 'true' },
  }).then((page) => page.rows);

export const patchOrganization = (
  id: string,
  patch: OrganizationPatch,
): Promise<OrganizationAdmin> =>
  apiRequest<WireOrganization>(API_ROUTES.patchOrganization.path(id), {
    method: API_ROUTES.patchOrganization.method,
    body: patch,
  });

export const deleteOrganization = (id: string): Promise<void> =>
  apiRequest<void>(API_ROUTES.deleteOrganization.path(id), {
    method: API_ROUTES.deleteOrganization.method,
  });

export const fetchMembers = (): Promise<MemberRow[]> =>
  apiRequest<WirePage<WireMember>>(API_ROUTES.members.path()).then((page) => page.rows);

export const patchMember = (id: string, patch: MemberPatch): Promise<void> =>
  apiRequest<void>(API_ROUTES.patchMember.path(id), {
    method: API_ROUTES.patchMember.method,
    body: patch,
  });

export const removeMember = (id: string): Promise<void> =>
  apiRequest<void>(API_ROUTES.deleteMember.path(id), {
    method: API_ROUTES.deleteMember.method,
  });

export const fetchInvitations = (): Promise<PendingInvitation[]> =>
  apiRequest<WirePage<WirePendingInvitation>>(API_ROUTES.invitations.path()).then(
    (page) => page.rows,
  );

export const cancelInvitation = (id: string): Promise<void> =>
  apiRequest<void>(API_ROUTES.cancelInvitation.path(id), {
    method: API_ROUTES.cancelInvitation.method,
  });

export const fetchNotifications = (): Promise<Notification[]> =>
  apiRequest<WirePage<WireNotification>>(API_ROUTES.notifications.path()).then((page) => page.rows);

/**
 * Just the number. Polled, so it is deliberately the cheapest call in the app — a partial index
 * over unread rows serves it, and it does not touch the list.
 */
export const fetchUnreadCount = (): Promise<number> =>
  apiRequest<{ unread: number }>(API_ROUTES.unreadCount.path()).then((body) => body.unread);

export const markNotificationRead = (id: string): Promise<void> =>
  apiRequest<void>(API_ROUTES.markNotificationRead.path(id), {
    method: API_ROUTES.markNotificationRead.method,
  });

export const markAllNotificationsRead = (): Promise<number> =>
  apiRequest<{ marked: number }>(API_ROUTES.markAllNotificationsRead.path(), {
    method: API_ROUTES.markAllNotificationsRead.method,
  }).then((body) => body.marked);

export const fetchUsers = (): Promise<PlatformUser[]> =>
  apiRequest<WirePage<WireUser>>(API_ROUTES.users.path()).then((page) =>
    // `?? null` because the two services deploy unordered: an API that predates suspension
    // omits the field, and `undefined` would render as neither suspended nor active.
    page.rows.map((row) => ({ ...row, suspendedAt: row.suspendedAt ?? null })),
  );

export const deleteUser = (id: string): Promise<void> =>
  apiRequest<void>(API_ROUTES.deleteUser.path(id), {
    method: API_ROUTES.deleteUser.method,
  });

/**
 * Suspend or restore an account.
 *
 * Global, not per-organization: a suspended person cannot sign in anywhere. Two routes rather
 * than one taking a boolean, so each is separately visible in the route contract and neither
 * can be reached by flipping a body field.
 */
export const suspendUser = (id: string): Promise<void> =>
  apiRequest<void>(API_ROUTES.suspendUser.path(id), {
    method: API_ROUTES.suspendUser.method,
  });

export const restoreUser = (id: string): Promise<void> =>
  apiRequest<void>(API_ROUTES.restoreUser.path(id), {
    method: API_ROUTES.restoreUser.method,
  });

export const fetchReminderPolicy = (): Promise<ReminderPolicyResponse> =>
  apiRequest<ReminderPolicyResponse>(API_ROUTES.reminderPolicy.path(), {
    method: API_ROUTES.reminderPolicy.method,
  });

/**
 * `undefined` keys are dropped by JSON.stringify, which is exactly the wire behaviour this
 * needs: an omitted setting is left alone, and an explicit `null` resets it to inherit. Sending
 * the whole form every time would turn "I changed one rung" into "I set every other field to
 * whatever the form happened to be showing".
 */
export const saveReminderPolicy = (
  patch: ReminderPolicySave,
): Promise<{ policy: ReminderPolicy }> =>
  apiRequest<{ policy: ReminderPolicy }>(API_ROUTES.saveReminderPolicy.path(), {
    method: API_ROUTES.saveReminderPolicy.method,
    body: JSON.stringify(patch),
  });

export const mintIngestKey = (projectId: string): Promise<{ credential: IngestCredential }> =>
  apiRequest<{ credential: IngestCredential }>(API_ROUTES.mintIngestKey.path(projectId), {
    method: API_ROUTES.mintIngestKey.method,
  });

export const revokeIngestKey = (projectId: string): Promise<void> =>
  apiRequest<void>(API_ROUTES.revokeIngestKey.path(projectId), {
    method: API_ROUTES.revokeIngestKey.method,
  });

// ------------------------------------------------------------------ staff billing audit

export interface TimeAuditListParams {
  month: string;
  project?: string;
  issue?: number;
  origin?: string;
  review?: string;
  session?: string;
  queue?: string;
  cursor?: string;
}

export const fetchTimeAuditSummary = (month: string): Promise<TimeAuditMonth> =>
  apiRequest<TimeAuditMonth>(API_ROUTES.timeAuditSummary.path(month), {
    method: API_ROUTES.timeAuditSummary.method,
  });

export const fetchTimeAuditEntries = (params: TimeAuditListParams): Promise<TimeAuditEntryPage> =>
  apiRequest<TimeAuditEntryPage>(API_ROUTES.timeAuditEntries.path(params.month), {
    method: API_ROUTES.timeAuditEntries.method,
    query: {
      project: params.project,
      issue: params.issue,
      origin: params.origin,
      review: params.review,
      session: params.session,
      queue: params.queue,
      cursor: params.cursor,
      limit: 50,
    },
  });

export const fetchTimeAuditEntry = (id: string): Promise<TimeAuditEntryDetail> =>
  apiRequest<TimeAuditEntryDetail>(API_ROUTES.timeAuditEntry.path(id), {
    method: API_ROUTES.timeAuditEntry.method,
  });

export const runTimeAuditSuggestions = (month: string): Promise<TimeAuditSuggestionRun> =>
  apiRequest<TimeAuditSuggestionRun>(API_ROUTES.suggestTimeAudit.path(month), {
    method: API_ROUTES.suggestTimeAudit.method,
  });

export const previewTimeAuditDecisions = (
  month: string,
  decisions: TimeAuditDecisionInput[],
): Promise<TimeAuditDecisionPreview> =>
  apiRequest<TimeAuditDecisionPreview>(API_ROUTES.previewTimeAuditDecisions.path(), {
    method: API_ROUTES.previewTimeAuditDecisions.method,
    body: { month, decisions },
  });

export const recordTimeAuditDecisions = (
  month: string,
  decisions: TimeAuditDecisionInput[],
  revision: string,
): Promise<TimeAuditDecisionPreview> =>
  apiRequest<TimeAuditDecisionPreview>(API_ROUTES.recordTimeAuditDecisions.path(), {
    method: API_ROUTES.recordTimeAuditDecisions.method,
    body: { month, decisions, revision },
  });

export const fetchTimeAuditAllowance = (month: string): Promise<TimeAuditAllowanceResponse> =>
  apiRequest<TimeAuditAllowanceResponse>(API_ROUTES.timeAuditAllowance.path(), {
    method: API_ROUTES.timeAuditAllowance.method,
    query: { month },
  });

export const recordTimeAuditAllowance = (input: {
  effectiveFrom: string;
  monthlyHours: number | null;
  timezone: string;
  note?: string | null;
}): Promise<{ allowance: TimeAuditAllowanceResponse['effective'] }> =>
  apiRequest<{ allowance: TimeAuditAllowanceResponse['effective'] }>(
    API_ROUTES.recordTimeAuditAllowance.path(),
    {
      method: API_ROUTES.recordTimeAuditAllowance.method,
      body: input,
    },
  );

export const downloadTimeAudit = (month: string): Promise<Blob> =>
  apiDownload(API_ROUTES.exportTimeAudit.path(month));

export const fetchBillingStatementPreview = (month: string): Promise<BillingStatementPreview> =>
  apiRequest<BillingStatementPreview>(API_ROUTES.previewBillingStatement.path(month), {
    method: API_ROUTES.previewBillingStatement.method,
  });

export const publishBillingStatement = (
  month: string,
  revision: string,
  internalNote?: string | null,
): Promise<{ statement: BillingStatementAudit }> =>
  apiRequest<{ statement: BillingStatementAudit }>(API_ROUTES.publishBillingStatement.path(month), {
    method: API_ROUTES.publishBillingStatement.method,
    body: { revision, ...(internalNote === undefined ? {} : { internalNote }) },
  });

export const downloadBillingStatement = (id: string): Promise<Blob> =>
  apiDownload(API_ROUTES.exportBillingStatement.path(id));
